import React, { forwardRef, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Slide,
  styled,
  Switch,
  Typography,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  TextField,
  IconButton,
  Pagination,
} from "@mui/material";
import {
  CustomInput,
  CustomSelect,
  IconContainer,
  PageTitle,
  PillRadio,
} from "../../components-new/common";
import { ScenarioIcon } from "../../components-new/icons";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  BrowserOptions,
  EnvOptions,
  ResolutionOptions,
} from "../../components-new/utils/constants";
import { useSelector, useDispatch } from "react-redux";
import useSaveTestCaseSettingsQuery from "../../useQuery/useSaveTestCaseSettingsQuery";
import useRemoveProcessingTCQuery from "../../useQuery/useRemoveProcessingTCQuery";
import useSaveUserExecutionSetupQuery from "../../useQuery/usesaveuserexecution";
import { useNotification, useUserPermissions, useDesignPrincipleImage } from "../../hooks";
import TestCaseSearch from "../../components-new/common/TestCaseSearch";
import StepDesignPrinciplesModal from "./StepDesignPrinciplesModal";
import {
  setPreProcessingConfig,
  setPostProcessingConfig,
  resetPreProcessingConfig,
  resetPostProcessingConfig,
} from "../../Redux/ProcessingSettings/ProcessingSettings";
import { setSelectedTestCaseItem } from "../../Redux/TestAuthoring/TestAuthoring";
import {
  useGetAvailableDevices,
  useGetAppsQuery,
  useProductQuery,
  useSystemidQuery,
  useGetEnvForModuleQuery,
  useModuleAuthorizationQuery,
  useDesignPrinciples,
  useGetSelectedPrinciplesForTestCaseQuery,
  useSaveTestCaseDesignPrinciplesMutation,
  useGetSelectedPrinciplesForTestStepQuery,
  useSaveTestStepDesignPrinciplesMutation,
  useGetBaseImageQuery,
  useGetStepBaseImageQuery,
} from "../../useQuery";
import { useQueryClient } from "@tanstack/react-query";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    width: "900px",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "auto",
    padding: "14px",
  },
  "& .MuiDialogContent-root": {
    overflowY: "scroll",
    paddingRight: "24px",
    width: "100%",
    "&::-webkit-scrollbar": {
      width: "8px",
      display: "block",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#555",
      },
    },
  },
}));

const StyledLabel = styled(Typography)`
  color: #8089a8;
  font-size: 13px;
  font-weight: 500;
`;

const StyledButton = styled(Button)`
  padding: 15px 28px;
  border-radius: 8px;
  text-transform: capitalize;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  line-height: 20px;
`;

const StyledTab = styled(Tab)({
  textTransform: "none",
  minWidth: 0,
  padding: "12px 16px",
  "&.Mui-selected": {
    color: "#ff5336",
  },
});

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ width: "100%", mt: 2 }}>
    {value === index && children}
  </Box>
);

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedTestCase = useSelector((state) => state.authoringSlice.selectedTestcaseItem);
  const initialUserExecutionRef = useRef({ useUserExecution: false, executionUrl: "" });
  const initialPrincipleIdsRef = useRef([]);
  const { mutate: saveTestCaseSettings, isSuccess } = useSaveTestCaseSettingsQuery();
  const { mutate: removeProcessingTC, isPending: isRemoveProcessingTCPending } =
    useRemoveProcessingTCQuery({
      onSuccess: () => {
        queryClient.invalidateQueries(["testCaseDetails"]);
        notify("Processing testcase removed successfully", "success");
      },
      onError: () => {
        notify("Failed to remove processing testcase", "error");
      },
    });
  const notify = useNotification();
  const [useUserExecution, setUseUserExecution] = useState(false);
  const [executionUrl, setExecutionUrl] = useState("");
  const [executionHubEnabled, setExecutionHubEnabled] = useState(false);
  const [executionType, setExecutionType] = useState("");
  const [executionHubUrl, setExecutionHubUrl] = useState("");
  const [executionSettings, setExecutionSettings] = useState(null);
  const selectedModule = useSelector((state) => state.authoringSlice.selectedComponent);
  const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const userType = userDetails?.usertype;

  const { data: moduleAuthorization } = useModuleAuthorizationQuery(selectedModule, {
    enabled: userType === 2 && !!selectedModule,
  });

  const { isReadOnly } = useUserPermissions({
    moduleId: selectedModule,
    moduleAuthorization,
  });
  const { mutate: saveUserExecution } = useSaveUserExecutionSetupQuery({
    onError: (err) => {
      console.error("Failed", err);
    },
  });
  const preProcessingConfig = useSelector((state) => state.processingSettings.preProcessing);
  const postProcessingConfig = useSelector((state) => state.processingSettings.postProcessing);
  const isMobile = useSelector((state) => state.authoringSlice.isMobile);

  const [open, setOpen] = useState(false);
  const [designPrincipals, setDesignPrincipals] = useState(false);
  const [isStepLevel, setIsStepLevel] = useState(false);
  const [testSteps, setTestSteps] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [stepPrinciples, setStepPrinciples] = useState({});
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [selectedPrincipleIds, setSelectedPrincipleIds] = useState([]);
  const [stepPage, setStepPage] = useState(1);
  const [principlePage, setPrinciplePage] = useState(1);
  const [imageSourceMode, setImageSourceMode] = useState(null); // null | "choosing" | "image" | "step"
  const [referenceStepId, setReferenceStepId] = useState(null);
  const { principlesQuery } = useDesignPrinciples();
  const { data: principlesData } = principlesQuery;

  const { data: selectedPrinciplesData } = useGetSelectedPrinciplesForTestCaseQuery(
    selectedTestCase?.idtest_case,
    {
      enabled: designPrincipals && open && !!selectedTestCase?.idtest_case,
    },
  );

  const { mutate: saveDesignPrinciples } = useSaveTestCaseDesignPrinciplesMutation();
  const { mutate: saveStepPrinciples, isPending: isSavingStep } =
    useSaveTestStepDesignPrinciplesMutation();
  const { data: selectedStepPrinciplesData } = useGetSelectedPrinciplesForTestStepQuery(
    selectedTestCase?.idtest_case,
    selectedStepId,
    { enabled: !!selectedStepId && stepModalOpen },
  );
  const { data: baseImageData, refetch: refetchBaseImage } = useGetBaseImageQuery(
    selectedTestCase?.idtest_case,
  );

  const { data: stepBaseImageData, refetch: refetchStepBaseImage } = useGetStepBaseImageQuery(
    selectedTestCase?.idtest_case,
    selectedStepId,
    { enabled: !!selectedStepId && stepModalOpen },
  );

  const {
    pendingImage,
    isUploading,
    existingBaseImage,
    handleImageUpload,
    handleConfirmUpload,
    handleRemoveImage,
  } = useDesignPrincipleImage({
    testCaseId: selectedTestCase?.idtest_case,
    principleLevel: "Test_case",
    fileName: baseImageData?.fileName,
    refetch: refetchBaseImage,
    notify,
    successMessage: "Image uploaded successfully",
  });

  const {
    pendingImage: stepPendingImage,
    isUploading: stepIsUploading,
    existingBaseImage: stepExistingBaseImage,
    pendingReferenceStepId: stepPendingReferenceStepId,
    handleImageUpload: handleStepImageUpload,
    handleConfirmUpload: handleConfirmStepUpload,
    handleRemoveImage: handleRemoveStepImage,
    handleSelectStepImage,
  } = useDesignPrincipleImage({
    testCaseId: selectedTestCase?.idtest_case,
    testStepId: selectedStepId,
    principleLevel: "Test_step",
    fileName: stepBaseImageData?.fileName,
    referenceStepId: stepBaseImageData?.referenceStepId,
    refetch: refetchStepBaseImage,
    notify,
    successMessage: "Step image uploaded successfully",
  });

  useEffect(() => {
    if (selectedPrinciplesData?.selectedPrinciples) {
      const activeIds = selectedPrinciplesData.selectedPrinciples
        .filter((p) => p.isActive)
        .map((p) => p.id);
      setSelectedPrincipleIds(activeIds);
      initialPrincipleIdsRef.current = activeIds;
    } else {
      setSelectedPrincipleIds([]);
      initialPrincipleIdsRef.current = [];
    }
  }, [selectedPrinciplesData]);

  useEffect(() => {
    if (selectedStepId && selectedStepPrinciplesData?.selectedPrinciples) {
      setStepPrinciples((prev) => {
        // Don't clobber a step the user has already loaded/edited this session
        if (prev[selectedStepId] !== undefined) return prev;
        const activeIds = selectedStepPrinciplesData.selectedPrinciples
          .filter((p) => p.isActive && String(p.testStepId) === String(selectedStepId))
          .map((p) => p.id);
        return { ...prev, [selectedStepId]: activeIds };
      });
    }
  }, [selectedStepId, selectedStepPrinciplesData]);

  useEffect(() => {
    setImageSourceMode(null);
    setReferenceStepId(null);
  }, [selectedStepId]);

  useEffect(() => {
    if (!stepModalOpen || !selectedStepId) return;
    if (stepExistingBaseImage) {
      setImageSourceMode("image");
    } else if (stepPendingReferenceStepId) {
      setImageSourceMode("image");
      setReferenceStepId(stepPendingReferenceStepId);
    }
  }, [stepModalOpen, selectedStepId, stepExistingBaseImage, stepPendingReferenceStepId]);

  const [selectedEnv, setSelectedEnv] = useState(selectedTestCase.envname);
  const [selectedBrowser, setSelectedBrowser] = useState(selectedTestCase.browser);
  const [selectedResolution, setSelectedResolution] = useState(
    selectedTestCase?.hasResolution === 1
      ? selectedTestCase?.selectedResolution?.toLowerCase()
      : "",
  );
  const [enableAudio, setEnableAudio] = useState(false);
  const [continueTest, setContinueTest] = useState(false);
  const [runOnce, setRunOnce] = useState(false);
  const [stealthMode, setStealthMode] = useState(false);
  const [captchaMode, setCaptchaMode] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [proxyUrl, setProxyUrl] = useState("");
  const [profileName, setProfileName] = useState("");
  const [aioTestCaseId, setAioTestCaseId] = useState("");
  const [envUrl, setEnvUrl] = useState("");
  const [description, setDescription] = useState("");
  const [forceNewSession, setForceNewSession] = useState(false);
  const [keepBrowserOpen, setKeepBrowserOpen] = useState(false);
  const [disablePopup, setDisablePopup] = useState(true);
  const [platform, setPlatform] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [processingTab, setProcessingTab] = useState(0);
  const [product, setProduct] = useState("");
  const [appVersion, setAppVersion] = useState("");
  const [systemid, setSystemid] = useState("");

  useEffect(() => {
    if (selectedTestCase && open) {
      setSelectedEnv(selectedTestCase?.envname ?? null);
      setSelectedBrowser(selectedTestCase?.browser ?? "");
      setEnableAudio(selectedTestCase?.enableaudio ?? false);
      setContinueTest(selectedTestCase?.continuetest ?? false);
      setRunOnce(selectedTestCase?.multirun === 0 ? true : false);
      setStealthMode(selectedTestCase?.stealthMode ?? false);
      setCaptchaMode(selectedTestCase?.captchaMode ?? false);
      setThreshold(selectedTestCase?.testcasethreshold ?? "");
      setProxyUrl(selectedTestCase?.proxyurl ?? "");
      setProfileName(selectedTestCase?.profilename ?? "");
      setAioTestCaseId(selectedTestCase?.AioTestCaseId ?? "");
      setEnvUrl(selectedTestCase?.envUrl ?? "");
      setDescription(selectedTestCase?.Test_Case_Desc ?? "");
      setForceNewSession(selectedTestCase?.forcenewsession ?? false);
      setKeepBrowserOpen(selectedTestCase?.keepbrowseropen ?? false);
      setDisablePopup(selectedTestCase?.disablePopup ?? true);
      setDesignPrincipals(selectedTestCase?.designPrincipals ?? false);
      setIsStepLevel(selectedTestCase?.principleLevel === "Test_step");
      setExecutionHubEnabled(executionSettings?.Execution_flag === 1);
      setExecutionType(executionSettings?.Execution_hub ?? "");
      setExecutionHubUrl(executionSettings?.Execution_url ?? "");

      setSelectedResolution(
        selectedTestCase?.hasResolution === 1
          ? selectedTestCase?.selectedResolution?.toLowerCase()
          : "",
      );

      setPlatform(selectedTestCase?.platform ?? null);
      setProduct(selectedTestCase?.mobileProductId ?? "");
      setAppVersion(selectedTestCase?.appId > 0 ? selectedTestCase.appId : "");
      try {
        const deviceData = selectedTestCase?.selectedDevice
          ? JSON.parse(selectedTestCase.selectedDevice)
          : null;
        const deviceKey = deviceData ? Object.keys(deviceData)[0] : "";
        setSystemid(deviceKey || "");
        setSelectedDevice((deviceKey && deviceData[deviceKey]) || "");
      } catch (e) {
        console.error("Failed to parse saved device configuration:", e);
        notify(
          "Couldn't load the previously saved device configuration. Please reselect System Id and Device before saving.",
          "warning",
        );
        setSystemid("");
        setSelectedDevice("");
      }

      dispatch(
        setPreProcessingConfig({
          testCase: selectedTestCase?.preProcessedTestCase || null,
          executeCondition:
            selectedTestCase?.executeConditionPre === "pass"
              ? "ifPass"
              : selectedTestCase?.executeConditionPre === "fail"
                ? "ifFail"
                : selectedTestCase?.executeConditionPre === "always"
                  ? "always"
                  : "",
        }),
      );
      dispatch(
        setPostProcessingConfig({
          testCase: selectedTestCase?.postProcessedTestCase || null,
          executeCondition:
            selectedTestCase?.executeConditionPost === "pass"
              ? "ifPass"
              : selectedTestCase?.executeConditionPost === "fail"
                ? "ifFail"
                : selectedTestCase?.executeConditionPost === "always"
                  ? "always"
                  : "",
        }),
      );
    }
  }, [open, selectedTestCase, executionSettings, dispatch]);

  useEffect(() => {
    if (isSuccess) {
      notify("Settings changes saved successfully", "success");
      queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
      queryClient.invalidateQueries({ queryKey: ["testCaseDetails"] });
      handleClose();
      dispatch(resetPreProcessingConfig());
      dispatch(resetPostProcessingConfig());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, dispatch]);

  const handleRemoveProcessingTC = (condition) => {
    removeProcessingTC({ tcid: selectedTestCase.idtest_case, condition });
  };

  const handleClickOpen = async () => {
    await queryClient.refetchQueries({ queryKey: ["testCaseResults"] });
    const freshData = queryClient.getQueryData([
      "testCaseResults",
      selectedTestCase?.idtest_case ?? selectedTestCase,
    ]);
    if (freshData?.testcase) {
      dispatch(setSelectedTestCaseItem(freshData.testcase));
    }
    const stepsArr = Object.values(freshData?.data ?? {}).map((step) => ({
      stepId: step.idtest_step,
      stepNumber: step.Step_Number,
      keyword: step.Keyword,
      action: step.Action,
    }));
    setTestSteps(stepsArr);
    setSelectedStepId(null);
    setStepPrinciples({});
    setExecutionSettings(freshData?.executionSettings ?? null);
    initialUserExecutionRef.current = { useUserExecution, executionUrl };
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleStepModalConfirm = () => {
    setStepModalOpen(false);
  };

  const handleStepModalCancel = () => {
    setStepModalOpen(false);
    setSelectedStepId(null);
  };

  const handleSaveStepPrinciples = () => {
    if (!selectedStepId) return;
    const ids = [...new Set(stepPrinciples[selectedStepId] || [])].join(",");
    saveStepPrinciples(
      {
        testCaseId: selectedTestCase.idtest_case,
        testStepId: selectedStepId,
        principleIds: ids,
      },
      {
        onSuccess: () => notify("Step principles saved successfully", "success"),
        onError: () => notify("Failed to save step principles", "error"),
      },
    );
  };

  const handleEnvChange = (event) => {
    setSelectedEnv(event.target.value);
  };

  const handleBrowserChange = (event) => {
    setSelectedBrowser(event.target.value);
  };

  const handleResolutionChange = (event) => {
    setSelectedResolution(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setProcessingTab(newValue);
  };

  const handlePreProcessingTestCaseSelect = (testCase) => {
    dispatch(setPreProcessingConfig({ testCase }));
  };

  const handlePostProcessingTestCaseSelect = (testCase) => {
    dispatch(setPostProcessingConfig({ testCase }));
  };

  const handlePreProcessingConditionChange = (event) => {
    dispatch(setPreProcessingConfig({ executeCondition: event.target.value }));
  };

  const handlePostProcessingConditionChange = (event) => {
    dispatch(setPostProcessingConfig({ executeCondition: event.target.value }));
  };

  const handleSave = () => {
    if (isMobile) {
      if (platform === "android" || platform === "ios") {
        if (!selectedDevice) {
          notify("Please select a device for the selected platform", "error");
          return;
        }
        if (!product) {
          notify("Please select a product for the selected device", "error");
          return;
        }
        if (!appVersion) {
          notify("Please select an app version for the selected product", "error");
          return;
        }
        if (!systemid) {
          notify("Please select an app version for the selected system id", "error");
          return;
        }
      }
    }

    const preProcessingTestCaseId =
      preProcessingConfig.testCase?.idtest_case || preProcessingConfig.testCase?.testCaseId;
    const postProcessingTestCaseId =
      postProcessingConfig.testCase?.idtest_case || postProcessingConfig.testCase?.testCaseId;

    if (preProcessingTestCaseId && !preProcessingConfig.executeCondition) {
      notify("Please select the execution condition for pre-processing", "error");
      return;
    }

    if (postProcessingTestCaseId && !postProcessingConfig.executeCondition) {
      notify("Please select the execution condition for post-processing", "error");
      return;
    }

    const payload = {
      EnvironmentName: selectedEnv,
      Browser: selectedBrowser,
      Resolution: selectedResolution,
      RunOnce: runOnce ? 0 : 1,
      stealthMode: stealthMode,
      captchaMode: captchaMode,
      Threshold: threshold,
      Proxy: proxyUrl,
      testCaseId: selectedTestCase.idtest_case,
      enablevideo: enableAudio,
      continuetest: continueTest,
      description: description,
      forcenewsession: forceNewSession,
      keepbrowseropen: keepBrowserOpen,
      disablePopup: disablePopup,
      platform: platform,
      selectedDevice: selectedDevice,
      appId: appVersion,
      device: { [systemid]: selectedDevice },
      preProcessedTestCase: {
        testCaseId: preProcessingConfig.testCase?.idtest_case || null,
        executeCondition:
          preProcessingConfig.executeCondition === "ifPass"
            ? "pass"
            : preProcessingConfig.executeCondition === "ifFail"
              ? "fail"
              : preProcessingConfig.executeCondition === "always"
                ? "always"
                : "",
      },
      postProcessedTestCase: {
        testCaseId: postProcessingConfig.testCase?.idtest_case || null,
        executeCondition:
          postProcessingConfig.executeCondition === "ifPass"
            ? "pass"
            : postProcessingConfig.executeCondition === "ifFail"
              ? "fail"
              : postProcessingConfig.executeCondition === "always"
                ? "always"
                : "",
      },
      ProfileName: profileName,
      AioTestCaseId: aioTestCaseId,
      designPrincipals: designPrincipals,
      principleLevel: isStepLevel ? "Test_step" : "Test_case",
      Execution_flag: executionHubEnabled ? 1 : 0,
      Execution_hub: executionType,
      Execution_url: executionHubUrl,
    };

    saveTestCaseSettings({ testCaseId: selectedTestCase.idtest_case, payload });

    const initial = initialUserExecutionRef.current;
    const userExecutionChanged =
      useUserExecution !== initial.useUserExecution || executionUrl !== initial.executionUrl;

    if (userExecutionChanged) {
      saveUserExecution({
        payload: {
          useUserExecution,
          executionUrl: useUserExecution ? executionUrl : null,
        },
      });
    }

    if (designPrincipals) {
      const current = [...selectedPrincipleIds].sort();
      const initial = [...initialPrincipleIdsRef.current].sort();
      const principlesChanged =
        current.length !== initial.length || current.some((id, i) => id !== initial[i]);

      if (principlesChanged) {
        saveDesignPrinciples({
          testCaseId: selectedTestCase.idtest_case,
          principleIds: selectedPrincipleIds.join(","),
        });
      }
    }
  };

  const {
    data: availableDevices,
    isLoading: loadingDevices,
    refetch: refetchDevices,
  } = useGetAvailableDevices(platform, {
    enabled: !!platform && platform !== null && platform !== undefined && open === true,
  });
  const { data: products } = useProductQuery();
  const { data: systemsid } = useSystemidQuery({ enabled: open === true });
  const {
    data: appVersions,
    isLoading: loadingApps,
    refetch: refetchApps,
  } = useGetAppsQuery(product, {
    enabled: !!product,
  });

  const { data: envData } = useGetEnvForModuleQuery(selectedTestCase?.Module, {
    enabled: !!selectedTestCase?.Module,
  });

  useEffect(() => {
    if (platform && open === true) {
      refetchDevices();
    }
  }, [platform, refetchDevices]);

  const STEPS_PER_PAGE = 8;
  const PRINCIPLES_PER_PAGE = 8;
  const stepPageCount = Math.ceil(testSteps.length / STEPS_PER_PAGE);
  const paginatedSteps = testSteps.slice(
    (stepPage - 1) * STEPS_PER_PAGE,
    stepPage * STEPS_PER_PAGE,
  );
  const allPrinciples = principlesData?.principles ?? [];
  const principlePageCount = Math.ceil(allPrinciples.length / PRINCIPLES_PER_PAGE);
  const paginatedPrinciples = allPrinciples.slice(
    (principlePage - 1) * PRINCIPLES_PER_PAGE,
    principlePage * PRINCIPLES_PER_PAGE,
  );

  return (
    <Box>
      <IconContainer
        handleClick={handleClickOpen}
        tooltipText="Settings"
        ariaLabel="Open Test Case Settings"
      >
        <ScenarioIcon color="#444141" />
      </IconContainer>
      <CustomDialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        maxWidth={false}
      >
        <PageTitle title={"Test Scenario Settings"} variant={"h5"} />
        <DialogContent sx={{ width: "100%", pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={3} width="100%">
            <Box>
              <StyledLabel>Environment</StyledLabel>
              <Box>
                {envData?.length === 0 ? (
                  <div>No environments found</div>
                ) : (
                  <>
                    <PillRadio
                      value="asRecorded"
                      label="As Recorded"
                      checked={selectedEnv == null}
                      onChange={() => setSelectedEnv(null)}
                      aria-label="Select As Recorded environment"
                      disabled={isReadOnly}
                    />
                    {envData?.map((option) => (
                      <Tooltip
                        key={option.idenvdetails}
                        title={option.envurl}
                        arrow
                        placement="top"
                      >
                        <span>
                          <PillRadio
                            value={option.envname}
                            label={option.envname}
                            checked={selectedEnv === option.idenvdetails}
                            onChange={() => setSelectedEnv(option.idenvdetails)}
                            aria-label={`Select ${option.envname} environment`}
                            disabled={isReadOnly}
                          />
                        </span>
                      </Tooltip>
                    ))}
                  </>
                )}
              </Box>
            </Box>

            <Box>
              <CustomInput
                labelinput="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                isDisabled={isReadOnly}
                ariaLabel="Enter description for the test scenario"
              />
            </Box>

            {!isMobile && (
              <Box>
                <StyledLabel>Browser</StyledLabel>
                <Box>
                  {BrowserOptions.map((option) => {
                    const browsers = selectedBrowser
                      ? selectedBrowser
                          .split(",")
                          .map((b) => b.trim())
                          .filter(Boolean)
                      : [];

                    const isDefault = browsers.length === 0;
                    const isAll = browsers.includes("ALL");

                    let checked = false;

                    if (option.value === "DEFAULT") {
                      checked = isDefault;
                    } else if (option.value === "ALL") {
                      checked = isAll;
                    } else {
                      checked = browsers.includes(option.value);
                    }

                    return (
                      <PillRadio
                        key={option.value}
                        value={option.value}
                        label={option.label}
                        checked={checked}
                        disabled={isReadOnly}
                        onChange={() => {}}
                        aria-label={`Select ${option.label} browser`}
                        onClick={(e) => {
                          e.preventDefault();
                          const browsers = selectedBrowser
                            ? selectedBrowser
                                .split(",")
                                .map((b) => b.trim())
                                .filter(Boolean)
                            : [];

                          let updatedBrowsers;

                          if (option.value === "DEFAULT") {
                            updatedBrowsers = [];
                          } else if (option.value === "ALL") {
                            updatedBrowsers = ["ALL"];
                          } else {
                            const isAll = browsers.includes("ALL");

                            if (isAll) {
                              updatedBrowsers = [option.value];
                            } else if (browsers.includes(option.value)) {
                              updatedBrowsers = browsers.filter((b) => b !== option.value);
                            } else {
                              updatedBrowsers = [...browsers, option.value];
                            }
                          }

                          const valueToStore =
                            updatedBrowsers.length === 0 ? null : updatedBrowsers.join(",");

                          setSelectedBrowser(valueToStore);
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            <Box>
              <StyledLabel>Screen Resolution</StyledLabel>
              <Box>
                {(isMobile
                  ? selectedTestCase?.resolution?.mobile
                  : selectedTestCase?.resolution?.web
                )?.length > 0 ? (
                  (isMobile
                    ? selectedTestCase?.resolution?.mobile
                    : selectedTestCase?.resolution?.web
                  )?.map((option) => (
                    <PillRadio
                      key={option.id}
                      value={`${option.width}x${option.height}`}
                      label={`${option.width}x${option.height}`}
                      checked={selectedResolution === `${option.width}x${option.height}`}
                      onChange={handleResolutionChange}
                      disabled={isReadOnly}
                      aria-label={`Select ${option.width}x${option.height} screen resolution`}
                    />
                  ))
                ) : (
                  <Box sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No resolutions available. Please{" "}
                      <Button
                        onClick={() =>
                          navigate("/new/admin", { state: { tab: 1, subTab: "resolution" } })
                        }
                        aria-label="Navigate to admin settings to add screen resolutions"
                        sx={{
                          color: "#1976d2",
                          textTransform: "none",
                          fontWeight: 500,
                          padding: 0,
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        add resolutions
                      </Button>{" "}
                      in Admin settings for better experience.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={runOnce}
                    onChange={() => setRunOnce((prev) => !prev)}
                    disabled={isReadOnly}
                    inputProps={{ "aria-label": "Toggle run test scenario once" }}
                  />
                }
                label="Run Once"
                labelPlacement="end"
              />
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={stealthMode}
                      onChange={() => setStealthMode((prev) => !prev)}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle stealth mode for test execution" }}
                    />
                  }
                  label="Run In Stealth Mode"
                  labelPlacement="end"
                />
              )}
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={captchaMode}
                      onChange={() => setCaptchaMode((prev) => !prev)}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle automatic captcha solving" }}
                    />
                  }
                  label="Auto Solve Captcha"
                  labelPlacement="end"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={continueTest}
                    onChange={() => setContinueTest((prev) => !prev)}
                    disabled={isReadOnly}
                    inputProps={{ "aria-label": "Toggle continue from previous test scenario" }}
                  />
                }
                label="Continue Previous Scenario"
                labelPlacement="end"
              />
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={enableAudio}
                      onChange={() => setEnableAudio((prev) => !prev)}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle audio and video recording" }}
                    />
                  }
                  label="Enable Audio/Video"
                  labelPlacement="end"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={forceNewSession}
                    onChange={() => setForceNewSession((prev) => !prev)}
                    disabled={isReadOnly}
                    inputProps={{ "aria-label": "Toggle force new session" }}
                  />
                }
                label="Run In New Session"
                labelPlacement="end"
              />
              {!isMobile && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={keepBrowserOpen}
                      onChange={() => setKeepBrowserOpen((prev) => !prev)}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle keep browser open on test step failure" }}
                    />
                  }
                  label="Keep Browser Open on Test Step Failure"
                  labelPlacement="end"
                />
              )}
              {(selectedTestCase?.caseType == null || selectedTestCase?.caseType === "WEB") && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={disablePopup}
                      onChange={() => setDisablePopup((prev) => !prev)}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle browser popup suppression" }}
                    />
                  }
                  label="Suppress Browser Popups"
                  labelPlacement="end"
                />
              )}
              <Box display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={designPrincipals}
                      onChange={() => setDesignPrincipals((prev) => !prev)}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle apply design principles" }}
                    />
                  }
                  label="Apply Design Principles"
                  labelPlacement="end"
                  sx={{ mr: 1 }}
                />
                {!isReadOnly && (
                  <Button
                    onClick={() => {
                      handleClose();
                      navigate("/new/admin", { state: { tab: 1, subTab: "designPrinciples" } });
                    }}
                    aria-label="Navigate to admin settings to view and manage design principles"
                    sx={{
                      color: "#1976d2",
                      textTransform: "none",
                      fontWeight: 500,
                      padding: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    (View Design principles)
                  </Button>
                )}
              </Box>

              {designPrincipals && (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={isStepLevel}
                      onChange={() => {
                        if (isStepLevel) {
                          setIsStepLevel(false);
                        } else {
                          setIsStepLevel(true);
                          setSelectedStepId(null);
                        }
                      }}
                      disabled={isReadOnly}
                      inputProps={{ "aria-label": "Toggle between case level and step level" }}
                    />
                  }
                  label={isStepLevel ? "Step Level" : "Case Level"}
                  labelPlacement="end"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={useUserExecution}
                    onChange={() => setUseUserExecution((prev) => !prev)}
                    disabled={isReadOnly}
                    inputProps={{ "aria-label": "Toggle use user execution hub" }}
                  />
                }
                label="Use User Execution"
                labelPlacement="end"
                sx={{ mr: 1 }}
              />

              {useUserExecution && (
                <TextField
                  fullWidth
                  size="small"
                  label="Execution URL"
                  placeholder="Enter execution hub URL"
                  value={executionUrl}
                  onChange={(e) => setExecutionUrl(e.target.value)}
                  sx={{ mt: 1 }}
                  disabled={isReadOnly}
                  aria-label="Enter execution hub URL for user execution"
                />
              )}

              {designPrincipals && (
                <Box mt={2} width="100%">
                  {!isStepLevel && (
                    <>
                      <StyledLabel>Select Principles to Apply</StyledLabel>
                      <Box
                        sx={{
                          maxHeight: "200px",
                          overflowY: "scroll",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 1,
                          p: 1,
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          backgroundColor: "#fafafa",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                            display: "block",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "#f1f1f1",
                            borderRadius: "8px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "#888",
                            borderRadius: "8px",
                            "&:hover": {
                              background: "#555",
                            },
                          },
                        }}
                      >
                        {!principlesData?.principles?.length && (
                          <Typography fontSize="13px" color="#8089A8" p={1}>
                            No principles available
                          </Typography>
                        )}
                        {principlesData?.principles?.map((p) => {
                          const isSelected = selectedPrincipleIds.includes(p.id);
                          return (
                            <Box
                              key={p.id}
                              display="flex"
                              alignItems="center"
                              px={1}
                              py={0.5}
                              sx={{
                                cursor: isReadOnly ? "default" : "pointer",
                                "&:hover": { backgroundColor: isReadOnly ? "" : "#f1f5f9" },
                                borderRadius: 1,
                              }}
                              onClick={() => {
                                if (isReadOnly) return;
                                setSelectedPrincipleIds((prev) =>
                                  isSelected ? prev.filter((id) => id !== p.id) : [...prev, p.id],
                                );
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                disabled={isReadOnly}
                                size="small"
                                sx={{ p: 0.5, mr: 1 }}
                                inputProps={{
                                  "aria-label": `Toggle ${p.principleDescription} design principle`,
                                }}
                              />
                              <Typography fontSize="13px" color="#334155">
                                {p.principleName}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>

                      <Box mt={2}>
                        <StyledLabel>Upload Image</StyledLabel>
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            border: "1px dashed #e2e8f0",
                            borderRadius: "8px",
                            backgroundColor: "#fafafa",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            "&:hover": { borderColor: isReadOnly ? "#e2e8f0" : "#ff5336" },
                            transition: "border-color 0.2s",
                          }}
                        >
                          {pendingImage ? (
                            <Typography fontSize="12px" color="#334155" flex={1}>
                              {pendingImage.name}
                            </Typography>
                          ) : existingBaseImage ? (
                            <Typography fontSize="12px" color="#334155" flex={1}>
                              {existingBaseImage.name}
                            </Typography>
                          ) : (
                            <Typography fontSize="12px" color="#8089A8" flex={1}>
                              No image selected
                            </Typography>
                          )}

                          {!isReadOnly && (
                            <>
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                aria-label={
                                  pendingImage || existingBaseImage
                                    ? "Change design principle image"
                                    : "Select design principle image"
                                }
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#ff5336",
                                  color: "#ff5336",
                                  "&:hover": { borderColor: "#e04a2f", backgroundColor: "#fff5f3" },
                                }}
                              >
                                {pendingImage || existingBaseImage
                                  ? "Change Image"
                                  : "Select Image"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  hidden
                                  onChange={handleImageUpload}
                                  aria-label="Upload design principle image"
                                />
                              </Button>

                              {pendingImage && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleConfirmUpload}
                                  disabled={isUploading}
                                  aria-label="Upload design principle image to server"
                                  sx={{
                                    textTransform: "none",
                                    backgroundColor: "#ff5336",
                                    color: "#fff",
                                    "&:hover": { backgroundColor: "#e04a2f" },
                                  }}
                                >
                                  {isUploading ? "Uploading..." : "Upload"}
                                </Button>
                              )}

                              {(pendingImage || existingBaseImage) && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={handleRemoveImage}
                                  aria-label="Delete design principle image"
                                  sx={{ background: "#fee2e2" }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                  {isStepLevel && (
                    <Box mt={2}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setStepPage(1);
                          setPrinciplePage(1);
                          setStepModalOpen(true);
                        }}
                        disabled={isReadOnly}
                        sx={{
                          textTransform: "none",
                          borderColor: "#ff5336",
                          color: "#ff5336",
                          "&:hover": { borderColor: "#e04a2f", backgroundColor: "#fff5f3" },
                        }}
                      >
                        Configure Steps
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {!isMobile && (
              <Box display="flex" gap={3}>
                <Box flex={1}>
                  <CustomInput
                    labelinput="Threshold"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    style={{ width: "60px" }}
                    isDisabled={isReadOnly}
                  />
                </Box>
                <Box flex={1}>
                  <CustomInput
                    labelinput="Proxy URL"
                    value={proxyUrl}
                    onChange={(e) => setProxyUrl(e.target.value)}
                    isDisabled={isReadOnly}
                  />
                </Box>
                <Box flex={1}>
                  <CustomInput
                    labelinput="Profile Name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    isDisabled={isReadOnly}
                  />
                </Box>
                <Box flex={1}>
                  <CustomInput
                    labelinput="AIO TestCaseId"
                    value={aioTestCaseId}
                    onChange={(e) => setAioTestCaseId(e.target.value)}
                    isDisabled={isReadOnly}
                  />
                </Box>
              </Box>
            )}

            {isMobile && (
              <Box>
                <Typography variant="h6">Configure Mobile Environment</Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 3,
                    mt: 2,
                  }}
                >
                  <Box>
                    <StyledLabel>Platform</StyledLabel>
                    <Box>
                      <PillRadio
                        value="android"
                        label="Android"
                        checked={platform === "android"}
                        onChange={() => {
                          setPlatform("android");
                          setSelectedDevice("");
                          setProduct("");
                        }}
                        disabled={isReadOnly}
                        aria-label="Select Android platform"
                      />
                      <PillRadio
                        value="ios"
                        label="iOS"
                        checked={platform === "ios"}
                        onChange={() => {
                          setPlatform("ios");
                          setSelectedDevice("");
                          setProduct("");
                        }}
                        disabled={isReadOnly}
                        aria-label="Select iOS platform"
                      />
                    </Box>
                  </Box>
                  <Box>
                    <StyledLabel>System Id</StyledLabel>
                    <CustomSelect
                      value={systemid ?? ""}
                      onChange={(e) => setSystemid(e.target.value)}
                      options={[
                        ...(systemsid?.length
                          ? systemsid.map((device) => ({
                              label: device,
                              value: device,
                            }))
                          : [{ label: "No devices available", value: "" }]),
                      ]}
                      placeholder="Select Device"
                      isLoading={loadingDevices}
                      width="100px"
                      disabled={isReadOnly}
                      aria-label="Select system device ID"
                    />
                  </Box>
                  <Box>
                    <StyledLabel>Available Devices</StyledLabel>
                    <CustomSelect
                      value={selectedDevice}
                      onChange={(e) => setSelectedDevice(e.target.value)}
                      options={[
                        { label: "Any Device", value: "any" },
                        ...(availableDevices?.length
                          ? availableDevices.map((device) => ({
                              label: device,
                              value: device,
                            }))
                          : [{ label: "No devices available", value: "" }]),
                      ]}
                      placeholder="Select Device"
                      isLoading={loadingDevices}
                      disabled={isReadOnly}
                      aria-label="Select available mobile device"
                    />
                  </Box>
                  <Box>
                    <StyledLabel>Product</StyledLabel>
                    <CustomSelect
                      value={product ?? ""}
                      variant={"outlined"}
                      onChange={(e) => {
                        setProduct(e.target.value);
                        refetchApps();
                      }}
                      disabled={!selectedDevice || isReadOnly}
                      options={
                        products?.map((product) => ({
                          label: product.productname,
                          value: product.idproducts,
                        })) || []
                      }
                      placeholder="Select Product"
                      isLoading={loadingApps}
                      fullWidth
                      aria-label="Select mobile app product"
                    />
                  </Box>
                  <Box>
                    <StyledLabel>Select App Version</StyledLabel>
                    <CustomSelect
                      value={appVersion ?? ""}
                      variant={"outlined"}
                      onChange={(e) => setAppVersion(e.target.value)}
                      disabled={!product || loadingApps || isReadOnly}
                      options={
                        appVersions?.map((app) => ({
                          label: `${app.appVersion} ${app.isLatestVersion == "yes" ? "Latest" : ""}`,
                          value: app.appId,
                        })) || []
                      }
                      aria-label="Select mobile app version"
                    />
                  </Box>
                </Box>
              </Box>
            )}
            {!isMobile && (
              <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={executionHubEnabled}
                    onChange={() => {
                      setExecutionHubEnabled((prev) => !prev);
                      setExecutionType("");
                      setExecutionHubUrl("");
                    }}
                    color="primary"
                    disabled={isReadOnly}
                    inputProps={{ "aria-label": "Toggle Local Execution Hub" }}
                  />
                  <StyledLabel>Local Execution Hub</StyledLabel>
                </Box>
                {executionHubEnabled ? (
                  <>
                    <CustomSelect
                      value={executionType}
                      onChange={(e) => {
                        setExecutionType(e.target.value);
                        setExecutionHubUrl("");
                      }}
                      options={[
                        { label: "Selenium Hub", value: "selenium" },
                        { label: "Selenoid", value: "selenoid" },
                      ]}
                      placeholder="Select Execution Type"
                      disabled={isReadOnly}
                    />
                    <CustomInput
                      labelinput=""
                      placeholder="Enter URL"
                      value={executionHubUrl}
                      onChange={(e) => setExecutionHubUrl(e.target.value)}
                      isDisabled={isReadOnly || !executionType}
                    />
                  </>
                ) : (
                  <PillRadio
                    value="Server"
                    label="Run on Server"
                    checked={true}
                    onChange={() => {}}
                    disabled={isReadOnly}
                    aria-label="Default execution hub"
                  />
                )}
              </Box>
            )}
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Test Case Processing
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "#8089A8",
                  fontSize: "13px",
                  mb: 2,
                }}
              >
                This feature applies only within a test suite execution context. It allows automatic
                pre/post test case execution to reset configurations or environment state when a
                test fails, ensuring that dependent test cases in the same suite do not fail due to
                residual side effects.
              </Typography>

              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={processingTab}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: "#ff5336",
                    },
                  }}
                >
                  <StyledTab label="Pre Processing" />
                  <StyledTab label="Post Processing" />
                </Tabs>
              </Box>

              <TabPanel value={processingTab} index={0}>
                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Select Test Case
                  </Typography>
                  <TestCaseSearch
                    onTestCaseSelect={handlePreProcessingTestCaseSelect}
                    initialValue={preProcessingConfig.testCase}
                    tabId="pre"
                    aria-label="Search and select test case for pre-processing"
                  />
                </Box>

                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Execution Condition
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={preProcessingConfig.executeCondition}
                      onChange={handlePreProcessingConditionChange}
                      aria-label="Select pre-processing execution condition"
                    >
                      <FormControlLabel
                        value="ifPass"
                        control={
                          <Radio
                            inputProps={{
                              "aria-label": "Execute pre-processing if previous test passes",
                            }}
                          />
                        }
                        label="If Pass"
                      />
                      <FormControlLabel
                        value="ifFail"
                        control={
                          <Radio
                            inputProps={{
                              "aria-label": "Execute pre-processing if previous test fails",
                            }}
                          />
                        }
                        label="If Fail"
                      />
                      <FormControlLabel
                        value="always"
                        control={
                          <Radio inputProps={{ "aria-label": "Always execute pre-processing" }} />
                        }
                        label="Always"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {!!selectedTestCase?.preProcessedTestCase?.testCaseId && (
                  <Box
                    mb={3}
                    sx={{
                      p: 2,
                      border: "1px solid #eaeaea",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      position: "relative",
                    }}
                  >
                    <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                      Current Pre-Processing Test Case
                    </Typography>
                    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveProcessingTC("pre")}
                        disabled={isReadOnly || isRemoveProcessingTCPending}
                        aria-label="Delete pre-processing test case"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2">
                        Test Case: {selectedTestCase.preProcessedTestCase.testcasename || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        ID: {selectedTestCase.preProcessedTestCase.testCaseId}
                      </Typography>
                      <Typography variant="body2">
                        Execute Condition:{" "}
                        {selectedTestCase.preProcessedTestCase.executeCondition || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TabPanel>

              <TabPanel value={processingTab} index={1}>
                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Select Test Case
                  </Typography>
                  <TestCaseSearch
                    onTestCaseSelect={handlePostProcessingTestCaseSelect}
                    initialValue={postProcessingConfig.testCase}
                    tabId="post"
                    aria-label="Search and select test case for post-processing"
                  />
                </Box>

                <Box mb={3}>
                  <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                    Execution Condition
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={postProcessingConfig.executeCondition}
                      onChange={handlePostProcessingConditionChange}
                      aria-label="Select post-processing execution condition"
                    >
                      <FormControlLabel
                        value="ifPass"
                        control={
                          <Radio
                            inputProps={{
                              "aria-label": "Execute post-processing if previous test passes",
                            }}
                          />
                        }
                        label="If Pass"
                      />
                      <FormControlLabel
                        value="ifFail"
                        control={
                          <Radio
                            inputProps={{
                              "aria-label": "Execute post-processing if previous test fails",
                            }}
                          />
                        }
                        label="If Fail"
                      />
                      <FormControlLabel
                        value="always"
                        control={
                          <Radio inputProps={{ "aria-label": "Always execute post-processing" }} />
                        }
                        label="Always"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {!!selectedTestCase?.postProcessedTestCase?.testCaseId && (
                  <Box
                    mb={3}
                    sx={{
                      p: 2,
                      border: "1px solid #eaeaea",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa",
                      position: "relative",
                    }}
                  >
                    <Typography color="#8089A8" fontSize="13px" fontWeight={500} mb={1}>
                      Current Post-Processing Test Case
                    </Typography>
                    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveProcessingTC("post")}
                        disabled={isReadOnly}
                        aria-label="Delete post-processing test case"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2">
                        Test Case: {selectedTestCase.postProcessedTestCase.testcasename || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        ID: {selectedTestCase.postProcessedTestCase.testCaseId}
                      </Typography>
                      <Typography variant="body2">
                        Execute Condition:{" "}
                        {selectedTestCase.postProcessedTestCase.executeCondition || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </TabPanel>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ width: "100%", mt: 2, px: 2 }}>
          <StyledButton
            sx={{ color: "#ff5336" }}
            variant="text"
            color="warning"
            onClick={handleClose}
            aria-label={
              isReadOnly
                ? "Close test scenario settings"
                : "Cancel changes and close test scenario settings"
            }
          >
            {isReadOnly ? "Close" : "Cancel"}
          </StyledButton>
          {!isReadOnly && (
            <StyledButton
              variant="contained"
              color="warning"
              sx={{
                backgroundColor: "#ff5336",
                color: "#ffffff",
                boxShadow: "0px 16px 30.6px 0px rgba(255, 83, 54, 0.27)",
              }}
              onClick={handleSave}
              aria-label="Save test scenario settings"
              endIcon={<BookmarkBorderIcon />}
            >
              Save
            </StyledButton>
          )}
        </DialogActions>
      </CustomDialog>

      <StepDesignPrinciplesModal
        open={stepModalOpen}
        onCancel={handleStepModalCancel}
        onConfirm={handleStepModalConfirm}
        testSteps={testSteps}
        selectedStepId={selectedStepId}
        setSelectedStepId={setSelectedStepId}
        stepPage={stepPage}
        setStepPage={setStepPage}
        stepPageCount={stepPageCount}
        paginatedSteps={paginatedSteps}
        principlesData={principlesData}
        paginatedPrinciples={paginatedPrinciples}
        principlePage={principlePage}
        setPrinciplePage={setPrinciplePage}
        principlePageCount={principlePageCount}
        stepPrinciples={stepPrinciples}
        setStepPrinciples={setStepPrinciples}
        isReadOnly={isReadOnly}
        stepPendingImage={stepPendingImage}
        stepExistingBaseImage={stepExistingBaseImage}
        stepPendingReferenceStepId={stepPendingReferenceStepId}
        stepIsUploading={stepIsUploading}
        handleStepImageUpload={handleStepImageUpload}
        handleConfirmStepUpload={handleConfirmStepUpload}
        handleRemoveStepImage={handleRemoveStepImage}
        handleSelectStepImage={handleSelectStepImage}
        handleSaveStepPrinciples={handleSaveStepPrinciples}
        isSavingStep={isSavingStep}
        imageSourceMode={imageSourceMode}
        setImageSourceMode={setImageSourceMode}
        referenceStepId={referenceStepId}
        setReferenceStepId={setReferenceStepId}
      />
    </Box>
  );
};

export default Settings;
